import React, {useMemo} from 'react';
import {statusLabel, statusStyle} from './calendarColors';
import {dayKey, formatTime, getMonthGridDays, isSameDay} from './calendarRange';
import {REST_HATCH, isDayClosed, restDaysForDay, restKindLabel} from './restDays';

// Month view (ported from the POS tablets' MonthView): classic Mon → Sun grid.
// Each cell lists its appointments (time + client, dot in the professional's
// colour) with a "+N" overflow link. Clicking a day opens it in the Day view.

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MAX_VISIBLE = 3;

const MonthView = ({
    currentDate,
    events,
    resources,      // visible (filtered) professionals
    onDayClick,     // (date) → open that day in the Day view
    onEventClick,
    restDays = [],      // employees' Repos / Congé, listed under the day number
    openingHours = null,// shop hours: closed days are hatched
}) => {
    const days = useMemo(() => getMonthGridDays(currentDate), [currentDate]);
    const month = currentDate.getMonth();
    const today = new Date();

    const resourceById = useMemo(() => {
        const map = {};
        resources.forEach(r => { map[r.id] = r; });
        return map;
    }, [resources]);

    const eventsByDay = useMemo(() => {
        const map = {};
        events.forEach(e => {
            if (!(e.start instanceof Date) || !resourceById[e.resourceId]) return;
            const key = dayKey(e.start);
            (map[key] = map[key] || []).push(e);
        });
        Object.values(map).forEach(list => list.sort((a, b) => a.start - b.start));
        return map;
    }, [events, resourceById]);

    return (
        <div className="flex-1 flex flex-col min-h-0 overflow-auto bg-white dark:bg-gray-900">
            {/* Weekday headers */}
            <div className="grid grid-cols-7 sticky top-0 z-10 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 min-w-[560px]">
                {WEEKDAYS.map(w => (
                    <div key={w} className="py-2 text-center text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700 last:border-r-0">
                        {w}
                    </div>
                ))}
            </div>

            {/* Day grid */}
            <div className="grid grid-cols-7 flex-1 min-w-[560px]" style={{gridAutoRows: 'minmax(7.5rem, 1fr)'}}>
                {days.map(day => {
                    const key = day.toDateString();
                    const list = eventsByDay[dayKey(day)] || [];
                    const visible = list.slice(0, MAX_VISIBLE);
                    const overflow = list.length - visible.length;
                    const isCurrentMonth = day.getMonth() === month;
                    const isToday = isSameDay(day, today);
                    const closed = isDayClosed(openingHours, day);
                    // "Repos: Sara, Ali" / "Congé: Nadia" for the visible pros
                    const restLines = ['Congé', 'Repos'].map(kind => ({
                        kind,
                        names: restDaysForDay(restDays, day)
                            .filter(r => resourceById[r.employee_id] && restKindLabel(r) === kind)
                            .map(r => resourceById[r.employee_id].name.split(' ')[0]),
                    })).filter(l => l.names.length > 0);
                    return (
                        <div key={key}
                             onClick={() => onDayClick(day)}
                             className={`flex flex-col gap-1 p-1.5 border-r border-b border-gray-200 dark:border-gray-700 cursor-pointer transition-colors hover:bg-primary/5 dark:hover:bg-primary/10 ${
                                 isToday ? 'bg-primary/[0.06] dark:bg-primary/10' : isCurrentMonth ? '' : 'bg-gray-50/70 dark:bg-gray-800/40'
                             }`}
                             style={closed ? REST_HATCH : undefined}>
                            <div className="flex items-center justify-between">
                                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold tabular-nums ${
                                    isToday ? 'bg-primary text-white'
                                        : isCurrentMonth ? 'text-gray-900 dark:text-gray-100'
                                        : 'text-gray-400 dark:text-gray-600'
                                }`}>
                                    {day.getDate()}
                                </span>
                                {closed ? (
                                    <span className="text-[11px] font-semibold text-red-500">Fermé</span>
                                ) : list.length > 0 && (
                                    <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{list.length} RDV</span>
                                )}
                            </div>
                            {restLines.map(line => (
                                <span key={line.kind} title={`${line.kind} : ${line.names.join(', ')}`}
                                      className={`truncate px-1.5 text-[11px] italic text-gray-500 dark:text-gray-400 ${isCurrentMonth ? '' : 'opacity-60'}`}>
                                    {line.kind === 'Congé' ? '🌴' : '🛌'} {line.kind} : {line.names.join(', ')}
                                </span>
                            ))}

                            {visible.map(event => {
                                const resource = resourceById[event.resourceId];
                                const st = statusStyle(event.status);
                                return (
                                    <button key={event.id} type="button"
                                            onClick={(e) => { e.stopPropagation(); onEventClick(event); }}
                                            title={`${formatTime(event.start)} – ${formatTime(event.end)} · ${event.clientName || event.title}${resource ? ` · ${resource.name}` : ''} · ${statusLabel(event.status)}`}
                                            className={`relative flex items-center gap-1.5 w-full min-w-0 pl-2.5 pr-1.5 py-0.5 rounded-md border text-left text-xs overflow-hidden
                                                        transition-shadow hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${st.card} ${
                                                isCurrentMonth ? '' : 'opacity-60'
                                            }`}>
                                        <span className="absolute left-0 top-0 bottom-0 w-1" style={{backgroundColor: resource?.color || '#4DB6AC'}}/>
                                        <span className="font-semibold tabular-nums opacity-70 flex-shrink-0">{formatTime(event.start)}</span>
                                        <span className={`flex-1 truncate font-medium ${event.status === 'cancelled' ? 'line-through' : ''}`}>
                                            {event.clientName || event.title}
                                        </span>
                                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${st.dot}`}/>
                                    </button>
                                );
                            })}

                            {overflow > 0 && (
                                <span className="px-1.5 text-xs font-semibold text-primary">
                                    +{overflow} autre{overflow > 1 ? 's' : ''}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default MonthView;
